import { ErrorHandler, Injectable } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Data } from '@angular/router';
import { IndexedDBRecoveryService } from './indexeddb-recovery.service';

@Injectable()
export class IndexedDBErrorHandler implements ErrorHandler {
  private errorShown = false; // Prevent duplicate error messages

  constructor(
    private snackBar: MatSnackBar,
    private indexedDBRecovery: IndexedDBRecoveryService,
  ) { }

  handleError(error: Data): void {
    const errorMessage = error?.message || error?.toString() || '';
    const errorStack = error?.stack || '';

    // Soft-handle Firestore auth/token race permission-denied (LOTTOSOCIAL-PWA-1MEE).
    // These are not real access denials; rules still enforce auth. Suppress so they
    // do not surface as unhandled Angular errors while Sentry beforeSend also filters.
    const isFirestorePermissionDenied =
      error?.code === 'permission-denied' ||
      (error?.name === 'FirebaseError' && (
        errorMessage.includes('Missing or insufficient permissions') ||
        errorMessage.includes('permission-denied')
      )) ||
      (typeof errorMessage === 'string' && (
        errorMessage.includes('Missing or insufficient permissions') ||
        errorMessage.includes('[code=permission-denied]')
      ));
    if (isFirestorePermissionDenied) {
      return;
    }

    if (this.indexedDBRecovery.isIndexedDBCorruptionError(error)) {
      this.indexedDBRecovery.handleCorruptionAndReload().catch((err) =>
        console.warn('[IndexedDB] Recovery failed:', err)
      );
      return;
    }

    const isFirebaseInstallationsError =
      (errorMessage.includes('undefined is not an object') && errorMessage.includes('createObjectStore')) ||
      (errorStack.includes('@firebase/installations') && errorMessage.includes('createObjectStore')) ||
      errorStack.includes('dbPromise.upgrade');

    // Check for general IndexedDB errors
    const isIndexedDBError =
      errorMessage.includes('IndexedDb') ||
      errorMessage.includes('Connection to Index') ||
      errorMessage.includes('Indexed Database server lost') ||
      (error?.code === 'unavailable' && errorMessage.includes('IndexedDbTransactionError'));

    if (isFirebaseInstallationsError) {
      // This is likely private browsing mode - show appropriate message
      if (!this.errorShown) {
        this.errorShown = true;
        this.snackBar.open(
          'Your browser is in private mode or has restricted storage. The app will continue with limited functionality.',
          'Dismiss',
          {
            duration: 7000,
            panelClass: ['warning-snackbar']
          }
        );
        
        // Reset flag after a delay
        setTimeout(() => { this.errorShown = false; }, 8000);
      }

      // Suppress the error from propagating further (handled gracefully)
      return;
    }

    if (isIndexedDBError) {
      // Show user-friendly error message for general IndexedDB errors
      if (!this.errorShown) {
        this.errorShown = true;
      this.snackBar.open(
        'Network connection issue detected. Some data may not be up-to-date. Please refresh the page.',
        'Dismiss',
        {
          duration: 5000,
          panelClass: ['warning-snackbar']
        }
      );
        
        // Reset flag after a delay
        setTimeout(() => { this.errorShown = false; }, 6000);
    }
    }
  }
}
